import React, { Suspense, lazy } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AppProvider } from "@/context/AppContext";
import Home from "@/pages/Home";
const BlogList = lazy(() => import("@/pages/BlogList"));
const BlogPost = lazy(() => import("@/pages/BlogPost"));
import AuthCallback from "@/pages/AuthCallback";
import { PackageBar } from "@/components/PackageBar";
import { ScrollToTop } from "@/components/ScrollToTop";
import { VisitTracker } from "@/hooks/useVisitTracking";

const Tours = lazy(() => import("@/pages/Tours"));
const TourDetail = lazy(() => import("@/pages/TourDetail"));
const CalendarPage = lazy(() => import("@/pages/CalendarPage"));
const Weekend = lazy(() => import("@/pages/Weekend"));
const Weekdays = lazy(() => import("@/pages/Weekdays"));
const Beginners = lazy(() => import("@/pages/Beginners"));
const Profile = lazy(() => import("@/pages/Profile"));
const Admin = lazy(() => import("@/pages/Admin"));
const LegalPage = lazy(() => import("@/pages/LegalPage"));
const PaymentSuccess = lazy(() => import("@/pages/Payment").then((m) => ({ default: m.PaymentSuccess })));
const PaymentCancel = lazy(() => import("@/pages/Payment").then((m) => ({ default: m.PaymentCancel })));
const Waiver = lazy(() => import("@/pages/Waiver"));
const DepartureWaiver = lazy(() => import("@/pages/DepartureWaiver"));
const ManageBooking = lazy(() => import("@/pages/ManageBooking"));
const Policies = lazy(() => import("@/pages/Policies"));
const RouteBySlug = lazy(() => import("@/pages/RouteBySlug"));
const PackagePage = lazy(() => import("@/pages/PackagePage"));
const GiftPage = lazy(() => import("@/pages/GiftPage"));
const MembershipsPage = lazy(() => import("@/pages/MembershipsPage"));
const SeatPick = lazy(() => import("@/pages/SeatPick"));
const MyTrips = lazy(() => import("@/pages/MyTrips"));
const BoardingPass = lazy(() => import("@/pages/BoardingPass"));
const NearCDMX = lazy(() => import("@/pages/NearCDMX"));

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Suspense fallback={<div className="min-h-screen bg-[#F7F5F0]" data-testid="page-loading" />}>
      <VisitTracker />
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/tours" element={<Tours />} />
        <Route path="/tour/:routeId" element={<TourDetail />} />
        <Route path="/calendario" element={<CalendarPage />} />
        <Route path="/fin-de-semana" element={<Weekend />} />
        <Route path="/entre-semana" element={<Weekdays />} />
        <Route path="/principiantes" element={<Beginners />} />
        <Route path="/blog" element={<BlogList />} />
        <Route path="/blog/:slug" element={<BlogPost />} />
        <Route path="/perfil" element={<Profile />} />
        <Route path="/admin" element={<Admin />} />
        <Route path="/payment/success" element={<PaymentSuccess />} />
        <Route path="/payment/cancel" element={<PaymentCancel />} />
        <Route path="/responsiva/:bookingId" element={<Waiver />} />
        <Route path="/responsiva-salida/:departureId" element={<DepartureWaiver />} />
        <Route path="/asientos/:departureId" element={<SeatPick />} />
        <Route path="/mis-aventuras" element={<MyTrips />} />
        <Route path="/pase/:bookingId/:code" element={<BoardingPass />} />
        <Route path="/gestionar-reserva" element={<ManageBooking />} />
        <Route path="/politicas-de-cancelacion" element={<Policies />} />
        <Route path="/aviso-de-privacidad" element={<LegalPage doc="privacy" />} />
        <Route path="/terminos-y-condiciones" element={<LegalPage doc="terms" />} />
        <Route path="/paquete" element={<PackagePage />} />
        <Route path="/regalo" element={<GiftPage />} />
        <Route path="/membresias" element={<MembershipsPage />} />
        <Route path="/cerca-de-ciudad-de-mexico" element={<NearCDMX />} />
        <Route path="/:slug" element={<RouteBySlug />} />
      </Routes>
    </Suspense>
  );
}
export default function App() {
  return (
    <BrowserRouter>
      <AppProvider>
        <ScrollToTop />
        <AppRouter />
        <PackageBar />
        <Toaster position="bottom-right" richColors />
      </AppProvider>
    </BrowserRouter>
  );
}
